import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Box, Grid, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CODE_MASTER_TYPE } from '@/constants/code-master-type';
import type { Reservation, TimeInformation } from '@/features/driver/types/driveDetail';
import { formatMoney, getTimelineData } from '@/features/driver/utils/processData';
import { useCodeMaster } from '@/modules/hooks/useCodeMaster';

type PassengerInfoShowProps = {
  reservation: Reservation[];
  isShow: boolean;
};

/**
 * 乗客情報表示画面
 */
export const PassengerInfoShow: React.FC<PassengerInfoShowProps> = ({ reservation, isShow }) => {
  const { t } = useTranslation();
  const [stationRoutes, setStationRoutes] = useState<TimeInformation[]>([]);

  const paymentMethodInVehicles = useCodeMaster(CODE_MASTER_TYPE.PAYMENT_METHOD_AT_RESERVATION);

  useEffect(() => {
    const timelineData = getTimelineData(reservation);
    setStationRoutes(timelineData);
  }, [reservation]);

  const getPaymentMethodName = (paymentMethod: string) => {
    const paymentMethodInVehicle = paymentMethodInVehicles?.find((item) => item.key === paymentMethod);

    return paymentMethodInVehicle?.value;
  };

  const handleIconClick = (index: number, row: number) => {
    setStationRoutes((prevState) => {
      const updatedInfo = [...prevState];
      const pickupInfo = { ...updatedInfo[index] };
      pickupInfo.pickup = [...pickupInfo.pickup];

      pickupInfo.pickup[row] = {
        ...pickupInfo.pickup[row],
        isShow: !pickupInfo.pickup[row].isShow,
      };

      updatedInfo[index] = pickupInfo;

      return updatedInfo;
    });
  };

  return (
    isShow && (
      <Box p={1} sx={{ width: '32.5%', background: '#F7F7F7', overflowY: 'auto', height: '100%' }}>
        {stationRoutes?.map((item, index) => (
          <Box key={item.key}>
            <Box sx={{ py: 1 }}>
              <Typography sx={{ fontWeight: '600', fontSize: 20 }}> {dayjs(item.time).format('HH:mm')}</Typography>
              {item.dropoff.length > 0 ? (
                <Typography
                  sx={{ color: '#EF6C00', fontWeight: '600', fontSize: 20 }}
                  style={{ paddingLeft: '0.6rem', paddingRight: '0.6rem', textAlign: 'left' }}
                >
                  {`${t('driveDetail.disembark')} ${item.waypointName}`}
                </Typography>
              ) : (
                <Typography
                  sx={{ color: '#0CB579', fontWeight: '600', fontSize: 20 }}
                  style={{ paddingLeft: '0.6rem', paddingRight: '0.6rem', textAlign: 'left' }}
                >
                  {`${t('driveDetail.embark')} ${item.waypointName}`}
                </Typography>
              )}
              {item.dropoff.length > 0 &&
                item.dropoff.map((dropoffInfo) => (
                  <Box key={dropoffInfo.reservationId} display="flex">
                    <Box width={24} />
                    <Typography component="div" sx={{ fontSize: 12, fontWeight: '600' }}>
                      <Box component="span" sx={{ marginRight: '1em' }}>
                        {`${t('driveDetail.disembark')}`}
                      </Box>
                      <Box
                        component="span"
                        sx={{ marginRight: '0.5em' }}
                      >{`${dropoffInfo.userName.split(' ')[0]}${t('driveDetail.mr')}`}</Box>
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 12 }}>
                      {(() => {
                        const totalPassengerCount = dropoffInfo.passengerTypeList.reduce(
                          (total, passenger) => total + passenger.passengerCount,
                          0,
                        );

                        return `${t('driveDetail.parenthesisLeft')}${totalPassengerCount}${t('driveDetail.people')}${t('driveDetail.parenthesisRight')}`;
                      })()}
                    </Typography>
                  </Box>
                ))}
              {item.pickup.length > 0 &&
                item.pickup.map((pickupInfo, row) => (
                  <Box key={pickupInfo.reservationId}>
                    <Box display="flex" sx={{ alignItems: 'center' }}>
                      <Box display="flex" alignItems="flex-end">
                        <IconButton onClick={() => handleIconClick(index, row)} size="small" sx={{ p: 0 }}>
                          {pickupInfo.isShow ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                        </IconButton>
                      </Box>

                      <Box>
                        <Typography component="div" sx={{ fontSize: 12, fontWeight: '600' }}>
                          <Box component="span" sx={{ marginRight: '1em' }}>
                            {`${t('driveDetail.embark')}`}
                          </Box>
                          <Box
                            component="span"
                            sx={{ marginRight: '0.5em' }}
                          >{`${pickupInfo.userName.split(' ')[0]}${t('driveDetail.mr')}`}</Box>
                          <Typography component="span" sx={{ fontSize: 12 }}>
                            {(() => {
                              const totalPassengerCount = pickupInfo.passengerTypeList.reduce(
                                (total, passenger) => total + passenger.passengerCount,
                                0,
                              );

                              return `${t('driveDetail.parenthesisLeft')}${totalPassengerCount}${t('driveDetail.people')}${t('driveDetail.parenthesisRight')}`;
                            })()}
                          </Typography>
                        </Typography>
                      </Box>
                    </Box>
                    {pickupInfo.isShow && (
                      <Box mx={2}>
                        <Grid
                          container
                          sx={{
                            border: '1px solid #C0C0C0',
                            borderRadius: '5px',
                            wordBreak: 'break-all',
                            padding: '5px 0 0 4px',
                          }}
                        >
                          <Grid item xs={12}>
                            <Typography sx={{ fontWeight: '600' }}>
                              {t('driveDetail.reservationId')}
                              <Typography component="span" sx={{ fontWeight: '600' }}>
                                {pickupInfo.reservationId}
                              </Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography sx={{ fontWeight: '600' }}>
                              {t('driveDetail.name')}
                              <Typography component="span">{`${pickupInfo.userName}${t('driveDetail.mr')}`}</Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12} display="flex">
                            <Typography noWrap sx={{ fontWeight: '600' }}>
                              {t('driveDetail.passenger')}
                            </Typography>
                            <Typography>
                              {pickupInfo.passengerTypeList.map((passenger, i) => (
                                <span key={passenger.passengerTypeId}>
                                  {`${passenger.passengerTypeName}${passenger.passengerCount}${t('driveDetail.people')}`}
                                  {i !== pickupInfo.passengerTypeList.length - 1 && <br />}
                                </span>
                              ))}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography sx={{ fontWeight: '600' }}>
                              {t('driveDetail.pickupTime')}
                              <Typography component="span">
                                {dayjs(pickupInfo.latestEstimatedPickupDatetime).format('HH:mm')}
                              </Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography sx={{ fontWeight: '600' }}>
                              {t('driveDetail.pickupWaypoint')}
                              <Typography component="span">{`${pickupInfo.pickupWaypointName}`}</Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography noWrap sx={{ fontWeight: '600' }}>
                              {t('driveDetail.dropoffTime')}
                              <Typography component="span">
                                {dayjs(pickupInfo.latestEstimatedDropoffDatetime).format('HH:mm')}
                              </Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography noWrap sx={{ fontWeight: '600' }}>
                              {t('driveDetail.dropoffWaypoint')}
                              <Typography component="span">{`${pickupInfo.dropoffWaypointName}`}</Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography noWrap sx={{ fontWeight: '600' }}>
                              {t('driveDetail.paymentMethod')}
                              <Typography component="span">
                                {getPaymentMethodName(String(pickupInfo.paymentMethod))}
                              </Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography noWrap sx={{ fontWeight: '600' }}>
                              {t('driveDetail.price')}
                              <Typography component="span">{`${formatMoney(pickupInfo.price)}${t('driveDetail.yen')}`}</Typography>
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography noWrap sx={{ fontWeight: '600' }}>
                              {t('driveDetail.message')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography>{pickupInfo.message}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>
                ))}
              {index !== stationRoutes.length - 1 && (
                <Box display="flex" justifyContent="space-between" pl={5}>
                  <Typography textAlign="center">↓</Typography>
                  <Typography textAlign="center">{`${t('driveDetail.timeRequired')}${item.timeDiff}${t('driveDetail.minutes')}`}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    )
  );
};
